import type { Sequence, SequenceFormData, SequenceEnrollment } from '@/types/entities/sequence';
import type { PaginatedResponse } from '@/types/entities';

import { ApiService } from './api.service';
import { endpoints } from './endpoints';

interface GetSequencesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

interface EnrollResult {
  enrolled: number;
  skipped: number;
}

/**
 * Marketing sequences API service
 */
export class SequenceApiService extends ApiService {
  /**
   * Get all sequences with pagination and filters
   */
  getSequences = async (params?: GetSequencesParams): Promise<PaginatedResponse<Sequence>> =>
    this.get(endpoints.sequences.filtered, { params });

  /**
   * Get a single sequence by ID
   */
  getSequenceById = async (id: string): Promise<Sequence> =>
    this.get(endpoints.sequences.byId(id));

  /**
   * Create a new sequence
   */
  createSequence = async (data: SequenceFormData): Promise<{ id: string }> =>
    this.post(endpoints.sequences.create, data);

  /**
   * Update a sequence
   */
  updateSequence = async (id: string, data: Partial<SequenceFormData>): Promise<Sequence> =>
    this.patch(endpoints.sequences.update(id), data);

  /**
   * Soft delete a sequence
   */
  deleteSequence = async (id: string): Promise<void> =>
    this.delete(endpoints.sequences.delete(id));

  /**
   * Activate a sequence
   */
  activateSequence = async (id: string): Promise<Sequence> =>
    this.post(endpoints.sequences.activate(id));

  /**
   * Pause a sequence
   */
  pauseSequence = async (id: string): Promise<Sequence> =>
    this.post(endpoints.sequences.pause(id));

  /**
   * Archive a sequence
   */
  archiveSequence = async (id: string): Promise<Sequence> =>
    this.post(endpoints.sequences.archive(id));

  /**
   * Duplicate a sequence
   */
  duplicateSequence = async (id: string): Promise<{ id: string }> =>
    this.post(endpoints.sequences.duplicate(id));

  /**
   * Enroll contacts into a sequence
   */
  enrollContacts = async (id: string, contactIds: string[]): Promise<EnrollResult> =>
    this.post(endpoints.sequences.enroll(id), { contactIds });

  /**
   * Unenroll a contact from a sequence
   */
  unenrollContact = async (id: string, enrollmentId: string): Promise<void> =>
    this.post(endpoints.sequences.unenroll(id, enrollmentId));

  /**
   * Get enrollments for a sequence
   */
  getEnrollments = async (
    id: string,
    params?: { page?: number; pageSize?: number; status?: string },
  ): Promise<PaginatedResponse<SequenceEnrollment>> =>
    this.get(endpoints.sequences.enrollments(id), { params });

  /**
   * Get sequence analytics
   */
  getStats = async (id: string): Promise<Record<string, any>> =>
    this.get(endpoints.sequences.stats(id));

  /**
   * Get sequence event log
   */
  getEvents = async (
    id: string,
    params?: { page?: number; pageSize?: number },
  ): Promise<PaginatedResponse<any>> =>
    this.get(endpoints.sequences.events(id), { params });

  /**
   * Test a sequence by sending a dry-run to a test email address
   */
  testSequence = async (
    id: string,
    data: { testEmail: string },
  ): Promise<{
    trace: Array<{ stepId: string; type: string; status: string; result?: any; error?: string }>;
    warnings?: string[];
  }> =>
    this.post(endpoints.sequences.test(id), data);
}

export const sequenceApiService = new SequenceApiService();
